import { useMemo } from 'react';
import { filterFamiliesByProject, filterFamiliesBySearch } from '../../../utils/filteringUtils';
import { getProjectFamilyIds } from '../../ProjectDashboard/utils/projectShareUtils';
import { compareValues, compareActivityItems, sortByName } from '../../../utils/sortingUtils';
import { buildTreeFromFeatures } from '../../../utils/treeUtils';
import { buildFamilyActivityLogs } from '../../../utils/activityUtils';

/**
 * Custom hook for derived data calculations
 * Computes filtered lists, selected family, and activity feeds
 */
export function useDerivedData(
    families,
    rulesets,
    features,
    projects,
    selectedFamilyId,
    activeProjectId,
    searchQuery,
    sortBy,
    sortOrder,
    activitySortBy,
    activitySortOrder,
    featureSearchQuery
) {
    // Enrich families with their last change date and author
    const familiesWithLastChange = useMemo(() => {
        return families.map(f => {
            let maxDate = new Date(f.updatedAt || f.createdAt || 0).getTime();
            let maxUser = f.updatedBy || 'System';

            // Check rulesets belonging to this family (use nested data from hierarchical endpoint)
            const fRulesets = f.ruleSets || [];
            fRulesets.forEach(rs => {
                const rsDate = new Date(rs.updatedAt || rs.createdAt || 0).getTime();
                if (rsDate > maxDate) {
                    maxDate = rsDate;
                    maxUser = rs.updatedBy || 'System';
                }
            });

            // Check features belonging to this family (use nested data from hierarchical endpoint)
            const fFeatures = f.features || [];
            fFeatures.forEach(feat => {
                const featDate = new Date(feat.updatedAt || feat.createdAt || 0).getTime();
                if (featDate > maxDate) {
                    maxDate = featDate;
                    maxUser = feat.updatedBy || 'System';
                }
            });

            return {
                ...f,
                lastChangeDate: maxDate > 0 ? new Date(maxDate).toISOString() : (f.createdAt || new Date().toISOString()),
                lastChangeUser: maxUser
            };
        });
    }, [families]);

    const selectedFamily = useMemo(() => {
        return familiesWithLastChange.find(f => f.id === selectedFamilyId) || null;
    }, [familiesWithLastChange, selectedFamilyId]);

    // Left selector sorted/filtered families list
    const filteredFamilies = useMemo(() => {
        let list = [...familiesWithLastChange];

        // Filter by active project if set
        if (activeProjectId) {
            list = filterFamiliesByProject(list, activeProjectId, projects);
        }

        // Search Filter
        list = filterFamiliesBySearch(list, searchQuery);

        // Sorting
        list.sort((a, b) => compareValues(a, b, sortBy, sortOrder));

        return list;
    }, [familiesWithLastChange, searchQuery, sortBy, sortOrder, activeProjectId, projects]);

    // Scoped metrics for selected family
    const familyRulesets = useMemo(() => {
        if (!selectedFamily) return [];
        // Use nested rulesets from selected family (hierarchical data)
        return selectedFamily.ruleSets || [];
    }, [selectedFamily]);

    const familyFeatures = useMemo(() => {
        if (!selectedFamily) return [];
        // Use nested features from selected family (hierarchical data)
        // Features are already scoped to the family in the hierarchical response
        return sortByName(selectedFamily.features || [], 'name');
    }, [selectedFamily]);

    const familyProjects = useMemo(() => {
        if (!selectedFamilyId) return [];
        return projects.filter(p => getProjectFamilyIds(p).some(id => String(id) === String(selectedFamilyId)));
    }, [projects, selectedFamilyId]);

    // Generate chronological activity feed (latest changes)
    const latestChanges = useMemo(() => {
        if (!selectedFamily) return [];
        const logs = buildFamilyActivityLogs(selectedFamily);
        logs.sort((a, b) => compareActivityItems(a, b, activitySortBy, activitySortOrder));
        return logs;
    }, [selectedFamily, activitySortBy, activitySortOrder]);

    // Scoped features hierarchial Tree construction
    const featuresTree = useMemo(() => {
        return buildTreeFromFeatures(familyFeatures, featureSearchQuery);
    }, [familyFeatures, featureSearchQuery]);

    return {
        familiesWithLastChange,
        selectedFamily,
        filteredFamilies,
        familyRulesets,
        familyFeatures,
        familyProjects,
        latestChanges,
        featuresTree
    };
}
